import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Box,
  IconButton,
  Stack,
  Divider,
  Paper,
  // 👇 NEW: Imported Dialog components for the confirmation modal
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { getBatchDetails, markOrderReady, cancelOrder } from "../../api/adminApi";
import AssignModal from "./AssignModal";

export default function BatchDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const [markingOrder, setMarkingOrder] = useState(null);

  // 👇 NEW: State to track which order ID the confirmation dialog is currently open for
  const [confirmOrderId, setConfirmOrderId] = useState(null);

  // Cancellation states
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await getBatchDetails(id);
      setData(res.data);
    } catch (err) {
      console.error("Batch details error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // 👇 UPDATED: This now fires only when the admin clicks "Yes" in the modal
  const handleConfirmMarkReady = async () => {
    if (!confirmOrderId) return;
    try {
      setMarkingOrder(confirmOrderId);
      await markOrderReady(confirmOrderId);

      setConfirmOrderId(null); // Close the dialog on success

      await fetchData(); // Seamlessly refreshes the batch data without a full page reload
    } catch (err) {
      console.error(err);
      alert("Failed to mark order ready. Please try again.");
    } finally {
      setMarkingOrder(null);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelOrderId || !cancelReason.trim()) {
      alert("Please enter a cancellation reason.");
      return;
    }
    try {
      setIsCancelling(true);
      await cancelOrder({
        order_id: cancelOrderId,
        reason: cancelReason.trim()
      });
      setCancelOrderId(null);
      setCancelReason("");

      await fetchData(); // Refresh batch details
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || err.response?.data?.message || "Failed to cancel order. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const getModernStatusStyle = (status) => {
    switch (status) {
      case "DELIVERED": return { bg: "#DDECC8", text: "#577A34" };
      case "ASSIGNED": return { bg: "#FFF6DE", text: "#F29D17" };
      case "CREATED": return { bg: "#E1F0FF", text: "#1976D2" };
      case "CANCELLED":
      case "REJECTED": return { bg: "#FFF0EE", text: "#E85D3F" };
      default: return { bg: "#F5F7FA", text: "#A59D96" };
    }
  };

  const getReadyStatusStyle = (status) => {
    return status === "FULL_READY"
      ? { bg: "#DDECC8", text: "#577A34" }
      : { bg: "#FFF6DE", text: "#F29D17" };
  };

  if (loading) return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="70vh" sx={{ width: "100%", bgcolor: '#FFFDF8' }}>
      <CircularProgress size={56} thickness={4} sx={{ color: '#F8C332' }} />
      <Typography variant="body1" sx={{ mt: 2, fontWeight: 800, color: '#3A2C23', letterSpacing: 0.5 }}>
        Loading batch details...
      </Typography>
    </Box>
  );

  if (!data) return (
    <Box sx={{ p: { xs: 2, sm: 4 }, minHeight: '100vh', bgcolor: '#FFFDF8' }}>
      <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '24px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
        <Typography color="#A59D96" fontWeight="800">No batch found or failed to load.</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2, fontWeight: 800, color: '#F29D17' }}>Go Back</Button>
      </Paper>
    </Box>
  );

  const batchStatusStyle = getModernStatusStyle(data.status);
  const readyStatusStyle = getReadyStatusStyle(data.ready_status);

  return (
    <Box p={{ xs: 2, sm: 3 }} sx={{ width: "100%", boxSizing: "border-box", minWidth: 0, overflowX: "hidden", minHeight: '100vh', bgcolor: '#FFFDF8', mb: 10 }}>

      {/* HEADER */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: '#FFFFFF',
            border: '1px solid #ECE5DA',
            color: '#3A2C23',
            boxShadow: '0 4px 8px rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease',
            width: 44,
            height: 44,
            mr: 2,
            '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332', transform: 'translateY(-2px)' }
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography variant="h5" fontWeight="900" sx={{ mt: 0.5, color: '#3A2C23' }}>
          Batch #{data.batch_id}
        </Typography>
      </Box>

      {/* STATUS CHIPS */}
      <Stack direction="row" spacing={1.5} mb={4} flexWrap="wrap" useFlexGap>
        <Chip
          label={data.status}
          sx={{
            fontWeight: "900",
            borderRadius: '8px',
            bgcolor: batchStatusStyle.bg,
            color: batchStatusStyle.text
          }}
        />
        <Chip
          label={data.ready_status.replace("_", " ")}
          sx={{
            fontWeight: "900",
            borderRadius: '8px',
            bgcolor: readyStatusStyle.bg,
            color: readyStatusStyle.text
          }}
        />
      </Stack>

      {/* ASSIGN BUTTON */}
      {data.ready_status === "FULL_READY" && data.status === "CREATED" && (
        <Button
          variant="contained"
          disableElevation
          sx={{
            mb: 4,
            borderRadius: '12px',
            fontWeight: "900",
            textTransform: "none",
            bgcolor: '#F8C332',
            color: '#3A2C23',
            px: 4,
            py: 1.2,
            boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
            '&:hover': { bgcolor: '#F5B71D' }
          }}
          onClick={() => setOpen(true)}
        >
          Assign Delivery
        </Button>
      )}

      <AssignModal
        open={open}
        setOpen={setOpen}
        batchId={data.batch_id}
        onSuccess={fetchData}
      />

      {/* ORDERS LIST */}
      <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23', mb: 2 }}>
        Orders in this Batch
      </Typography>

      {data.orders?.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '24px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
          <Typography color="#A59D96" fontWeight="800">No orders attached to this batch.</Typography>
        </Paper>
      )}

      <Stack spacing={2} pb={4}>
        {data.orders?.map((o) => {
          const orderStyle = getModernStatusStyle(o.status);

          return (
            <Card
              key={o.order_id}
              elevation={0}
              sx={{
                borderRadius: '24px',
                border: "none",
                bgcolor: '#FFFFFF',
                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 }, "&:last-child": { pb: { xs: 2.5, sm: 3 } } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23' }}>
                    Order #{o.order_id}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1.5}>
                    {/* 👇 UPDATED: Changed styling to green theme and onClick to open the modal */}
                    {o.ready_status !== "READY" && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={markingOrder === o.order_id}
                        onClick={() => setConfirmOrderId(o.order_id)}
                        sx={{
                          borderRadius: '12px', textTransform: 'none', fontWeight: 900,
                          bgcolor: "#DDECC8", color: "#577A34", borderColor: "#CCE3AF", px: 2,
                          "&:hover": { bgcolor: "#CCE3AF", borderColor: "#577A34" }
                        }}
                      >
                        {markingOrder === o.order_id ? <CircularProgress size={14} color="inherit" /> : "Mark Ready"}
                      </Button>
                    )}

                    {/* Cancellation Button */}
                    {o.status !== "DELIVERED" && o.status !== "CANCELLED" && (
                      <Button variant="outlined" size="small" onClick={() => setCancelOrderId(o.order_id)}
                        sx={{
                          borderRadius: "12px", textTransform: "none", fontWeight: 900,
                          bgcolor: "#FFF0EE", color: "#E85D3F", borderColor: "#F5D0C9", px: 2,
                          "&:hover": { bgcolor: "#F5D0C9", borderColor: "#E85D3F" }
                        }}>
                        Cancel
                      </Button>
                    )}
                    <Chip
                      label={o.status}
                      size="small"
                      sx={{
                        fontWeight: "800",
                        fontSize: '0.7rem',
                        height: 24,
                        borderRadius: '6px',
                        bgcolor: orderStyle.bg,
                        color: orderStyle.text
                      }}
                    />
                  </Box>

                </Box>

                <Divider sx={{ mb: 2, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                  ORDER ITEMS
                </Typography>

                <Stack spacing={1.5}>
                  {o.items?.map((i, idx) => {
                    const isItemReady = i.status === "READY";

                    return (
                      <Box key={idx} display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="body2" fontWeight="700" sx={{ color: '#3A2C23', pr: 2, flex: 1, wordBreak: 'break-word' }}>
                          {i.name} <Typography component="span" fontWeight="900" sx={{ color: '#E85D3F' }}>x{i.quantity}</Typography>
                        </Typography>

                        <Typography
                          variant="body2"
                          fontWeight="800"
                          sx={{
                            color: isItemReady ? '#577A34' : '#F29D17',
                            bgcolor: isItemReady ? '#DDECC8' : '#FFF6DE',
                            px: 1,
                            py: 0.5,
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {i.status}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* 👇 NEW: BEAUTIFUL YES/NO CONFIRMATION DIALOG 👇 */}
      <Dialog
        open={Boolean(confirmOrderId)}
        onClose={() => !markingOrder && setConfirmOrderId(null)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 2,
            minWidth: "320px",
            textAlign: "center",
            boxShadow: "0 10px 32px rgba(0,0,0,0.2)"
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "900",
            color: "#3A2C23",
            fontSize: "1.4rem",
            pb: 1
          }}
        >
          Mark as Ready?
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" fontWeight="700" color="#6E655E" mb={1}>
            Are you sure you want to manually mark Order #{confirmOrderId} as READY?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button
            variant="outlined"
            disabled={Boolean(markingOrder)}
            onClick={() => setConfirmOrderId(null)}
            sx={{
              borderRadius: "12px",
              color: "#6E655E",
              borderColor: "#ECE5DA",
              fontWeight: "900",
              px: 4,
              py: 1,
              "&:hover": { bgcolor: "#FFFDF8", borderColor: "#A59D96" }
            }}
          >
            No
          </Button>

          <Button
            variant="contained"
            disableElevation
            disabled={Boolean(markingOrder)}
            onClick={handleConfirmMarkReady}
            sx={{
              borderRadius: "12px",
              bgcolor: "#DDECC8",
              color: "#577A34",
              fontWeight: "900",
              px: 4,
              py: 1,
              "&:hover": { bgcolor: "#CCE3AF" },
              "&.Mui-disabled": { bgcolor: "#F5F7FA", color: "#A59D96" }
            }}
          >
            {markingOrder ? <CircularProgress size={20} color="inherit" /> : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog
        open={Boolean(cancelOrderId)}
        onClose={() => !isCancelling && setCancelOrderId(null)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 2,
            minWidth: "320px",
            boxShadow: "0 10px 32px rgba(0,0,0,0.2)"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23", fontSize: "1.4rem", pb: 1, textAlign: "center" }}>
          Cancel Order #{cancelOrderId}?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" fontWeight="700" color="#6E655E" mb={2} textAlign="center">
            Please provide a reason for cancelling this order.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="e.g. Customer requested cancellation via phone call."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            disabled={isCancelling}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontWeight: 600,
                color: "#3A2C23",
                "& fieldset": { borderColor: "#ECE5DA", borderWidth: "2px" },
                "&:hover fieldset": { borderColor: "#A59D96" },
                "&.Mui-focused fieldset": { borderColor: "#E85D3F" }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button
            variant="outlined"
            disabled={isCancelling}
            onClick={() => setCancelOrderId(null)}
            sx={{
              borderRadius: "12px", color: "#6E655E", borderColor: "#ECE5DA", fontWeight: "900", px: 4, py: 1,
              "&:hover": { bgcolor: "#FFFDF8", borderColor: "#A59D96" }
            }}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={isCancelling || !cancelReason.trim()}
            onClick={handleCancelOrder}
            sx={{
              borderRadius: "12px", bgcolor: "#E85D3F", color: "#FFFFFF", fontWeight: "900", px: 4, py: 1,
              "&:hover": { bgcolor: "#D84A2C" },
              "&.Mui-disabled": { bgcolor: "#F5F7FA", color: "#A59D96" }
            }}
          >
            {isCancelling ? <CircularProgress size={20} color="inherit" /> : "Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}